import { create } from 'zustand';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import { toast } from '@/components/ui/Toast';
import { useAuthStore } from '@/stores';
import * as Sentry from '@sentry/react';
import type { OnboardingStep, UserType } from '@/types';

export interface OnboardingClass {
  id: string;
  name: string;
  color: string;
  documentIds: string[];
}

interface OnboardingState {
  currentStep: OnboardingStep;
  userType: UserType | null;
  classes: OnboardingClass[];
  isSubmitting: boolean;

  setStep: (step: OnboardingStep) => void;
  setUserType: (type: UserType) => void;
  addClass: (cls: OnboardingClass) => void;
  removeClass: (index: number) => void;
  updateClass: (index: number, updates: Partial<OnboardingClass>) => void;
  addDocumentToClass: (classIndex: number, documentId: string) => void;
  completeOnboarding: (userId: string) => Promise<boolean>;
  reset: () => void;
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  currentStep: 'user-type',
  userType: null,
  classes: [],
  isSubmitting: false,

  setStep: (currentStep) => set({ currentStep }),

  setUserType: (userType) => set({ userType }),

  addClass: (cls) => set((state) => ({
    classes: [...state.classes, { ...cls, id: crypto.randomUUID() }],
  })),

  removeClass: (index) => set((state) => ({
    classes: state.classes.filter((_, i) => i !== index),
  })),

  updateClass: (index, updates) => set((state) => ({
    classes: state.classes.map((cls, i) => i === index ? { ...cls, ...updates } : cls),
  })),

  addDocumentToClass: (classIndex, documentId) => set((state) => ({
    classes: state.classes.map((cls, i) =>
      i === classIndex && !cls.documentIds.includes(documentId)
        ? { ...cls, documentIds: [...cls.documentIds, documentId] }
        : cls
    ),
  })),

  completeOnboarding: async (userId: string) => {
    set({ isSubmitting: true });

    const { userType, classes } = get();

    try {
      // 1. Create classes and associate documents first (before marking onboarded)
      const failedCreations: string[] = [];
      const docLinkWarnings: string[] = [];
      for (const cls of classes) {
        if (!cls.name.trim()) continue;

        const { data: created, error: classError } = await supabase
          .from('classes')
          .insert({
            user_id: userId,
            name: cls.name,
            color: cls.color,
            archived: false,
          })
          .select()
          .single();

        if (classError || !created) {
          reportSupabaseError(classError, `completeOnboarding.createClass[${cls.name}]`);
          failedCreations.push(cls.name);
          continue;
        }

        // Associate documents with the class
        if (cls.documentIds.length > 0) {
          const classDocuments = cls.documentIds.map((docId, idx) => ({
            class_id: created.id,
            document_id: docId,
            document_type: 'material' as const,
            order_index: idx,
          }));

          const { error: docError } = await supabase.from('class_documents').insert(classDocuments);
          if (docError) {
            reportSupabaseError(docError, `completeOnboarding.classDocuments[${cls.name}]`);
            docLinkWarnings.push(cls.name);
          }
        }
      }

      const classesAttempted = classes.filter(cls => cls.name.trim()).length;
      if (failedCreations.length > 0 && failedCreations.length >= classesAttempted && classesAttempted > 0) {
        toast.error('Failed to create your classes. Please try again.');
        return false;
      }

      // Show warnings for partial failures (but continue with onboarding)
      if (failedCreations.length > 0) {
        toast.error(`Failed to create: ${failedCreations.join(', ')}. You can add them later from settings.`);
      } else if (docLinkWarnings.length > 0) {
        toast.error(`Documents could not be linked to: ${docLinkWarnings.join(', ')}. You can add them later.`);
      }

      // 2. Mark profile as onboarded (only after classes succeed or partially succeed)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          user_type: userType,
          onboarded: true,
        })
        .eq('id', userId);

      if (profileError) {
        reportSupabaseError(profileError, 'completeOnboarding.profileUpdate');
        toast.error('Failed to save your profile. Please try again.');
        return false;
      }

      // 3. Update auth store
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.getState().setUser({
          ...currentUser,
          onboarded: true,
          user_type: userType || undefined,
        });
      }

      get().reset();
      return true;
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'onboarding.completeOnboarding' } });
      toast.error('Something went wrong completing setup. Please try again.');
      return false;
    } finally {
      set({ isSubmitting: false });
    }
  },

  reset: () => set({
    currentStep: 'user-type',
    userType: null,
    classes: [],
    isSubmitting: false,
  }),
}));
